import { useEffect, useRef } from 'react';

import { fireConfetti } from './confetti.helper';
import styles from './ConfettiCanvas.module.scss';

interface ConfettiCanvasProps {
  /**
   * Opaque key — fires a confetti burst each time this changes to a non-null
   * value. Initialize to null to skip firing on mount (e.g. same-day reloads).
   */
  fireKey: string | null;
  className?: string;
}

/**
 * Absolutely-positioned canvas that fires a confetti burst whenever `fireKey`
 * changes to a non-null value. Parent must be `position: relative` with a
 * defined height.
 */
export const ConfettiCanvas: React.FC<ConfettiCanvasProps> = ({ fireKey, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cannonRef = useRef<ReturnType<typeof fireConfetti> | null>(null);
  const prevKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!fireKey || fireKey === prevKeyRef.current) return;
    prevKeyRef.current = fireKey;
    const canvas = canvasRef.current;
    if (!canvas) return;
    cannonRef.current?.reset();
    cannonRef.current = fireConfetti(canvas);
    return () => {
      cannonRef.current?.reset();
      cannonRef.current = null;
    };
  }, [fireKey]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`${styles.canvas}${className ? ` ${className}` : ''}`}
    />
  );
};
